import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Search, Mic, ShieldCheck } from "lucide-react";
import { LogoMark, LogoText } from "@/components/Logo";
import { InstallAppButton } from "@/components/InstallAppButton";

const SCREENS = [
  { title: "Clicou, Guardou!", text: "Tudo que você não quer esquecer, em um só lugar.", icon: null },
  { title: "Fale, escreva ou fotografe.", text: "O Clicou, Guardou! organiza para você.", icon: Mic },
  { title: "Encontre depois.", text: "Pergunte naturalmente. A gente lembra.", icon: Search },
  { title: "Mais tranquilidade no seu dia.", text: "Pequenas coisas hoje. Mais tranquilidade amanhã.", icon: ShieldCheck },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const last = step === SCREENS.length - 1;
  const s = SCREENS[step];

  useEffect(() => {
    if (localStorage.getItem("ga_onboarded")) navigate("/entrar", { replace: true });
  }, [navigate]);

  const finish = () => {
    localStorage.setItem("ga_onboarded", "1");
    navigate("/entrar");
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-7 pb-10 pt-14 text-white" style={{ background: "linear-gradient(180deg,#05324A 0%,#074160 55%,#0A6373 100%)" }} data-testid="onboarding-screen-container">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5" aria-hidden="true">
          {SCREENS.map((_, i) => (
            <span key={i} className="h-1.5 rounded-full transition-all duration-300" style={{ width: i === step ? 22 : 8, background: i === step ? "#13C9A3" : "rgba(255,255,255,.3)" }} />
          ))}
        </div>
        {!last && (
          <button onClick={finish} className="text-sm text-white/70" data-testid="onboarding-skip-button">Pular</button>
        )}
      </div>

      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }} className="flex flex-col items-center">
            {step === 0 ? (
              <>
                <LogoMark size={140} />
                <div className="mt-8 whitespace-nowrap text-4xl"><LogoText light /></div>
                <p className="mt-2 text-sm text-white/70">Você vive, a gente lembra.</p>
              </>
            ) : (
              <div className="flex h-36 w-36 items-center justify-center rounded-[36px]" style={{ background: "rgba(255,255,255,.08)" }}>
                <s.icon size={64} color="#33D9B2" strokeWidth={1.8} />
              </div>
            )}
            <h1 className="mt-10 text-3xl font-semibold leading-tight" data-testid="onboarding-title">{step === 0 ? "" : s.title}</h1>
            <p className="mt-3 max-w-xs text-base text-white/80" data-testid="onboarding-text">{s.text}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {step === 0 && (
        <ul className="mb-8 space-y-3 text-sm text-white/85">
          {[["Guarde o que for importante", Bell], ["Encontre quando precisar", Search], ["Receba lembretes", Bell]].map(([t, I]) => (
            <li key={t} className="flex items-center gap-3"><I size={18} color="#33D9B2" />{t}</li>
          ))}
        </ul>
      )}

      {step === 0 && <div className="mb-3" data-testid="onboarding-install-app"><InstallAppButton light /></div>}
      <button onClick={() => (last ? finish() : setStep(step + 1))} className="ga-btn-primary w-full text-base uppercase tracking-wide" data-testid="onboarding-next-button">
        {last ? "Começar" : "Continuar"}
      </button>
      <p className="mt-4 text-center text-sm text-white/70">
        Já tem uma conta?{" "}
        <button onClick={finish} className="font-semibold" style={{ color: "#33D9B2" }} data-testid="onboarding-login-link">Entrar</button>
      </p>
    </div>
  );
}
